import loadable from '@loadable/component';
import { type FunctionComponent, useContext, useEffect, useState } from 'react';
import { LinkButton } from '../../../components/buttons/link-button/link-button.component';
import { PageContainer } from '../../../components/common-components/page/page-container/page-container.component';
import { type ProductPageType } from '../../../components/common-components/page/page-container/page-container.types';
import { DynamicYieldPlacement } from '../../../components/dynamic-yield/placement/dynamic-yield-placement.component';
import { DyProductRecommendations } from '../../../components/dynamic-yield/product-recommendations/dy-product-recommendations.component';
import { ProductFinish } from '../../../components/product-components/product-finish/product-finish.component';
import { ProductKeySpecs } from '../../../components/product-components/product-key-specs/product-key-specs.component';
import { ProductMedia } from '../../../components/product-components/product-media/product-media.component';
import { ProductPrice } from '../../../components/product-components/product-price/product-price.component';
import { ProductSaleBanner } from '../../../components/product-components/product-sale-banner/product-sale-banner.component';
import { ProductSummary } from '../../../components/product-components/product-summary/product-summary.component';
import { SaleRestrictions } from '../../../components/product-components/sale-restrictions/sale-restrictions.component';
import { ProductRebate } from '../../../components/rebates/product-rebate/product-rebate.component';
import { SearchProductLists } from '../../../components/search-components/search-product-lists/search-product-lists.component';
import { OVERVIEW_SECTION_ID } from '../../../constants/product';
import { ProductFamilyContext } from '../../../contexts/product-family/product-family.context';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { ANALYTICS_PAGE_NAMES } from '../../../helpers/analytics/analytics.helper';
import { ADD_TO_CART_BUTTON_TYPE } from '../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import {
	generateSelectionFromRequiredGroups,
	getConfigurableProductAnalyticsData
} from '../../../helpers/product-helper/configuration/product-configuration.helper';
import { resolveConfigureUrl } from '../../../helpers/product-helper/priced-options.helper';
import {
	convertRegionalStatusToAvailabilityEnum,
	createProductName,
	createProductPageTitle,
	getFlattenedAttributes,
	isShowroomOnlyProduct
} from '../../../helpers/product-helper/product.helper';
import { useAddToCartButton } from '../../../hooks/add-to-cart/add-to-cart-button.hooks';
import { useCustomAddProductToCart } from '../../../hooks/add-to-cart/add-to-cart.hooks';
import { useConfiguration } from '../../../hooks/configuration/configuration.hooks';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { type PlaProductFamily } from '../../../types/product.types';
import { PlaControls } from '../components/pla-controls/pla-controls.component';

export type ProductSummaryPageProps = {
	product: PlaProductFamily;
	productUid: number;
	regionId?: string;
};

const LoadableCartRecentlyUpdated = loadable(
	() =>
		import(
			/* webpackChunkName: "cartrecentlyupdated" */ '../../../components/cart-components/cart-recently-updated/cart-recently-updated.component'
		),
	{
		resolveComponent: ({ CartRecentlyUpdatedPanel }) => CartRecentlyUpdatedPanel
	}
);

const LoadableShowroomContent = loadable(
	() =>
		import(
			/* webpackChunkName: "showroomproductinformation" */ '../../../components/showroom-product-information/showroom-product-information.component'
		),
	{
		resolveComponent: ({ ShowroomProductInformation }) => ShowroomProductInformation
	}
);

export const ProductSummaryPage: FunctionComponent<ProductSummaryPageProps> = ({ product, productUid, regionId }) => {
	const { selectedVariant, currentVariant } = useContext(VariantContext);
	const { status } = useConfiguration({
		familyId: product.id,
		queryFilters: { excludeGroupType: ['REQUIRED'], urlFormat: 'PLA' }
	});
	const [cartQuantity, setCartQuantity] = useState<number | undefined>(1);
	const [isAddingToCart, setIsAddingToCart] = useState(false);
	const { productFamily } = useContext(ProductFamilyContext);

	const addToCartAnalyticsData = getConfigurableProductAnalyticsData(
		{ variantId: selectedVariant.id, quantity: cartQuantity },
		{ productFamily },
		ANALYTICS_PAGE_NAMES.PRODUCT_LISTING_AD
	);

	const [configurableAddToCart, resetAddToCart, addToCartStatus] = useCustomAddProductToCart({
		variantId: selectedVariant.id,
		analyticsData: addToCartAnalyticsData,
		quantity: cartQuantity
	});

	const currentSelection =
		status.data?.getProductConfiguration &&
		generateSelectionFromRequiredGroups(
			status.data.getProductConfiguration.required,
			{
				variantId: selectedVariant.id,
				variantPrice: selectedVariant.priceInfo.current,
				variantQuantity: cartQuantity,
				id: product.id
			},
			currentVariant.bundleItems.length > 0
		);

	const newItemIds = addToCartStatus.data?.addProductToCart?.newItemIds;

	const [units, setUnits] = useState(0);

	const navigate = useNavigation();

	function setAndTrackCartQuantity(newQuantity) {
		return setCartQuantity(newQuantity);
	}

	const { buttonType, addToCartActionStatus } = useAddToCartButton({ pageName: 'Product-PLA', onClick: addToCart });

	useEffect(() => {
		resetAddToCart();
		// resetAddToCart needs to only run on mount
	}, []);

	const beginAddToCart = () => {
		// pre-fetch the cart updated panel while we execute add-to-cart
		LoadableCartRecentlyUpdated.preload();
		setIsAddingToCart(true);
	};

	/**
	 * Add To Cart Handler
	 */
	function addToCart() {
		if (product.isConfigurable) {
			return navigate(resolveConfigureUrl({ path: product.url, variantId: selectedVariant.id, quantity: cartQuantity }));
		} else if (currentSelection) {
			beginAddToCart();
			return configurableAddToCart(currentSelection, ADD_TO_CART_BUTTON_TYPE.PLA).finally(() => setIsAddingToCart(false));
		}
		return null;
	}
	const brandName = product.brandInfo.name;
	const metaDescription = `Save on the ${brandName} ${selectedVariant.modelNumber} from Build.com. Low Prices + Fast & Free Shipping on Most Orders. Find reviews, expert advice, manuals, specs & more.`;
	const isRegionallyOutOfStock = regionId && selectedVariant.regionalStatus === 'OutOfStock';
	const breadcrumbs = [...product.breadcrumbs, { name: `${brandName} ${product.productId}` }];

	const shouldDisplayShowroomContent = isShowroomOnlyProduct(selectedVariant);

	const displayableKeySpecs = getFlattenedAttributes(productFamily.attributeGroups).filter(({ isHidden }) => !isHidden);

	return (
		<PageContainer<ProductPageType>
			brandName={brandName}
			canonicalURL={product.url}
			imagePublicId={selectedVariant.image.id}
			isInStock={true}
			itemAvailability={convertRegionalStatusToAvailabilityEnum(String(selectedVariant.regionalStatus))}
			metaDescription={metaDescription}
			modelNumber={selectedVariant.modelNumber}
			pageTitle={createProductPageTitle(brandName, selectedVariant.modelNumber, product.title)}
			price={selectedVariant.priceInfo.current}
			productDescription={product.title}
			productId={`${product.id}`}
			productName={createProductName(brandName, selectedVariant.modelNumber)}
			productUrl={selectedVariant.url}
			ratingCount={product.reviewSummary.reviewCount}
			ratingValue={product.reviewSummary.ratingValue}
			breadcrumbs={breadcrumbs}
			showUpdateLocation={true}>
			<>
				{/* Product Section */}
				{shouldDisplayShowroomContent ? (
					<LoadableShowroomContent
						product={product}
						productUid={productUid}
						currentVariant={currentVariant}
						currentVariantCountAvailable={currentVariant.count}
					/>
				) : (
					<section data-testid="pla-product-section">
						<div className="cf flex flex-column db-ns">
							<div className="fl w-60 w-50-ns order-1 w-100" data-testid="pla-product-section-container">
								{/* Product Image */}
								<ProductMedia product={product} />
							</div>
							<div className="fl w-40 w-50-ns order-0 w-100" data-testid="pla-product-section-details-container">
								<div>
									{/* Product Details */}
									<ProductSummary
										brand={brandName}
										modelNumber={selectedVariant.modelNumber}
										title={product.title}
										shouldClampTitle={true}
										reviewSummary={product.reviewSummary}
										url={product.url}
										selectedVariant={selectedVariant.id}
										productUid={selectedVariant.id}
									/>
								</div>
							</div>
							<div className="fl-ns w-50-ns order-2" data-testid="pla-product-section-price-container">
								<div>
									<div className={'flex flex-row flex-wrap w-100 f4 f3-ns'}>
										<ProductPrice {...currentVariant?.priceInfo} variantId={currentVariant?.id} className={`mr4 mt2`} />
									</div>
								</div>
								{!isRegionallyOutOfStock && selectedVariant && selectedVariant.saleRestrictions && (
									<SaleRestrictions
										{...selectedVariant.saleRestrictions}
										currentFamilyId={product.id}
										className={`mv3`}
										selectedVariant={selectedVariant}
									/>
								)}
								<div>{selectedVariant && <ProductRebate variantIds={[selectedVariant.id]} />}</div>

								<ProductFinish productUid={productUid} isRegionallyOutOfStock={isRegionallyOutOfStock || false} />

								{/* PlaControls houses ShippingEligibility, AddToCart  */}
								<PlaControls
									isAddingToCart={isAddingToCart}
									onAddToCartClick={addToCart}
									cartQuantity={cartQuantity}
									setAndTrackCartQuantity={setAndTrackCartQuantity}
									setCartQuantity={setCartQuantity}
									resetAddToCart={resetAddToCart}
									addToCartAction={buttonType}
									addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.PLA}
									addToCartActionStatus={addToCartActionStatus}
									units={{ unitsValue: units, setUnits }}
									product={product}
									isRegionallyOutOfStock={isRegionallyOutOfStock}
									shouldDisplayMoreDetailsButton={displayableKeySpecs.length === 0}
								/>

								<div className={'mt3'}>
									<ProductSaleBanner productSales={currentVariant.sales} />
								</div>
								{displayableKeySpecs.length > 0 && (
									<>
										<ProductKeySpecs specs={displayableKeySpecs} />
										<div className="mt3">
											<LinkButton
												size="SMALL"
												buttonType="button"
												buttonStyle="SECONDARY"
												url={`${productFamily.url}?uid=${currentVariant.id}#${OVERVIEW_SECTION_ID}`}>
												See More Details
											</LinkButton>
										</div>
									</>
								)}
							</div>
						</div>
					</section>
				)}
				{status.data && currentSelection && newItemIds && (
					<LoadableCartRecentlyUpdated
						id={product.id}
						newItemIds={newItemIds}
						selection={currentSelection}
						filterOverride={{ excludeGroupType: ['REQUIRED'], urlFormat: 'PLA' }}
					/>
				)}
				<DyProductRecommendations trackingId="pla" variantId={selectedVariant.id} campaign="React PLA Page" />
				<SearchProductLists />
				<DynamicYieldPlacement dynamicYieldId="dy-recs-pla-page-bottom" />
			</>
		</PageContainer>
	);
};
